// src/pages/CategoriesList.js
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Search, Users, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Meteors } from '../components/ui/meteors';
import Navbar from '../components/Navbar';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

export default function CategoriesList() {
  const [searchTerm, setSearchTerm] = useState('');

  const { darkMode, toggleDarkMode } = useTheme();

  // Auth Context
  const { user, userLoading, openLogin } = useAuth();

  const { data: categories, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/servicecategory/`);
      return res.data;
    },
  });

  const filteredCategories = categories?.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.description && cat.description.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  const pageTitle = "Service Categories in Kenya | Hire Trusted Professionals - SErraND";
  const pageDescription = "Browse all professional service categories on SErraND Kenya. Find verified plumbers, electricians, beauty experts, mechanics, cleaners and more near you.";
  const pageKeywords = "service categories Kenya, hire professionals Nairobi, plumbers Kenya, electricians Mombasa, beauty services Kenya, SErraND categories";
  const canonicalUrl = "https://www.serrand.org/categories";
  const ogImage = "https://www.serrand.org/static/images/og-image.jpg";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Marvin Useki" />
        <meta name="viewport" content="width=device-width, initial-scale=0.9, maximum-scale=1.0, user-scalable=yes" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="SErraND" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:site" content="@SErraND" />
      </Helmet>

      {/* Meteors Background */}
      <Meteors count={40} angle={220} />

      {/* Navbar */}
      <Navbar
        user={user}
        userLoading={userLoading}
        isAuthLoading={userLoading}
        handleLogout={() => { /* handle logout if needed */ }}
        openLogin={openLogin}
        openRegister={openLogin} // You can adjust this if you have a separate register handler
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
      />

      <div className="relative z-10 min-h-screen bg-background pt-16">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-background border-b">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 text-foreground">
              Find the Right Professional
            </h1>
            <p className="text-2xl text-muted-foreground max-w-3xl mx-auto mb-10">
              Browse trusted service categories across Kenya and connect with verified providers in minutes.
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search categories (e.g., plumbing, beauty, mechanics...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-7 text-base rounded-2xl"
              />
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="py-16 px-4 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">All Service Categories</h2>
              <p className="text-muted-foreground mt-1">
                {filteredCategories.length} categories available
              </p>
            </div>
            {searchTerm && (
              <Button variant="ghost" onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredCategories.map((cat) => (
                <Link key={cat.slug} to={`/categories/${cat.slug}`}>
                  <Card className="group h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-border hover:border-primary/30 overflow-hidden">
                    {cat.image && (
                      <div className="h-52 overflow-hidden">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}

                    <CardContent className="p-6 flex flex-col h-[calc(100%-13rem)]">
                      <CardTitle className="text-xl mb-3 group-hover:text-primary transition-colors">
                        {cat.name}
                      </CardTitle>

                      <CardDescription className="line-clamp-3 flex-1">
                        {cat.description || 'Professional services in this category'}
                      </CardDescription>

                      <div className="flex items-center justify-between mt-6 pt-6 border-t text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>{cat.provider_count || 0} providers</span>
                        </div>
                        <span className="text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                          Browse <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {!isLoading && filteredCategories.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-muted-foreground">No categories found matching your search.</p>
            </div>
          )}
        </section>

        {/* Trust / SEO Section */}
        <section className="py-16 bg-muted/50 border-t">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-semibold mb-6">Why Choose SErraND Categories?</h2>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="space-y-3">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold">Verified Professionals</h3>
                <p className="text-sm text-muted-foreground">
                  Every provider is vetted with real reviews and trust scores.
                </p>
              </div>
              <div className="space-y-3">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <ArrowRight className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold">Fast & Local</h3>
                <p className="text-sm text-muted-foreground">
                  Connect with experts near you in Nairobi, Mombasa, Kisumu and beyond.
                </p>
              </div>
              <div className="space-y-3">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  🔒
                </div>
                <h3 className="font-semibold">Secure Booking</h3>
                <p className="text-sm text-muted-foreground">
                  Transparent pricing, secure payments, and dispute protection.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
